/**
 * AICopilotHandoffBanner.jsx
 *
 * Two pieces of UI rendered above the chat composer:
 *
 *  1. Handoff banner — shows when the selected conversation has
 *     `needs_human_handoff = true`. Renders the reason and a
 *     "Clear handoff" button.
 *
 *  2. Handoff request button — small chip next to the composer that
 *     marks the conversation as needing a human and notifies the
 *     assignee. Disabled when the conversation is already in handoff.
 *
 * Conditionally rendered: only when `ai_copilot_settings.enabled` AND
 * `human_handoff_enabled` are true.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Hand, X, Loader2, AlertOctagon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

export default function AICopilotHandoffBanner({ conversation, onChange }) {
  const [enabled, setEnabled] = useState(false);
  const [known, setKnown] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/ai-copilot/settings`, { headers: authHeaders() });
        if (cancelled) return;
        const s = res.data || {};
        setEnabled(!!(s.enabled && s.human_handoff_enabled));
      } catch {
        setEnabled(false);
      } finally {
        if (!cancelled) setKnown(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const requestHandoff = useCallback(async () => {
    if (!conversation?.id) return;
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/ai-copilot/handoff`,
        { conversation_id: conversation.id, reason: 'operator_request' },
        { headers: authHeaders() });
      toast.success('Conversation flagged for human handoff');
      onChange && onChange({ ...conversation, needs_human_handoff: true,
        handoff_reason: 'operator_request' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to request handoff');
    } finally {
      setBusy(false);
    }
  }, [conversation, onChange]);

  const clearHandoff = useCallback(async () => {
    if (!conversation?.id) return;
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/ai-copilot/handoff/clear`,
        { conversation_id: conversation.id },
        { headers: authHeaders() });
      toast.success('Handoff cleared');
      onChange && onChange({ ...conversation, needs_human_handoff: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear handoff');
    } finally {
      setBusy(false);
    }
  }, [conversation, onChange]);

  if (!known || !enabled || !conversation) return null;

  if (conversation.needs_human_handoff) {
    return (
      <div className="mb-1.5 flex items-start gap-2 px-2.5 py-1.5 rounded border border-rose-300 bg-rose-50 text-rose-900"
           data-testid="ai-handoff-banner">
        <AlertOctagon className="h-3.5 w-3.5 mt-0.5 flex-none text-rose-700" />
        <div className="flex-1 text-[11px] sm:text-xs min-w-0">
          <span className="font-semibold">Human handoff needed</span>
          {conversation.handoff_reason && (
            <span className="text-rose-800/80 ml-1 truncate">· {String(conversation.handoff_reason).replaceAll('_', ' ')}</span>
          )}
          <div className="text-[10px] sm:text-xs text-rose-800/70 hidden sm:block">
            AI auto-reply is paused on this thread until cleared.
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={clearHandoff} disabled={busy}
                className="text-rose-800 hover:bg-rose-100 h-6 px-1.5" data-testid="ai-handoff-clear-btn">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
          <span className="ml-1 hidden sm:inline">Clear</span>
        </Button>
      </div>
    );
  }

  return (
    <Button variant="ghost" size="sm" onClick={requestHandoff} disabled={busy}
            className="h-6 px-1.5 text-[10px] gap-1 text-wt-text-muted hover:text-rose-700"
            title="Request human handoff"
            data-testid="ai-handoff-request-btn">
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Hand className="h-3 w-3" />}
      <span className="hidden sm:inline">Handoff</span>
    </Button>
  );
}
